import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Card, Form, ListGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getRunbooksAPI, markRunbookUsefulAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { platformUrl } from '../../../utils/mhNav';
import styles from './Runbooks.module.scss';

const CATS = ['All', 'Microsoft', 'Google', 'WhatsApp', 'Jobs', 'Billing', 'Auth'];

export default function Runbooks() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRunbooksAPI({ search, category });
      setList(res.data || []);
      setActive((cur) => {
        if (cur && res.data?.some((r) => r.id === cur.id)) return cur;
        return res.data?.[0] || null;
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => {
    load();
  }, [load]);

  const canEdit = localStorage.getItem('mh_platform_admin') === '1';

  if (error && !list.length) {
    return (
      <div>
        <PageHeader title="Runbooks" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Support' }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Runbooks" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Support', path: platformUrl('support') }]} />

      <div className={styles.layout}>
        <div className={styles.listCol}>
          <Card>
            <Card.Body>
              <Form.Control className="mb-2" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
              <Form.Select className="mb-2" value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
              {loading && !list.length ? (
                <LoadingSkeleton rows={5} cols={1} />
              ) : (
                <ListGroup variant="flush">
                  {list.map((r) => (
                    <ListGroup.Item
                      key={r.id}
                      action
                      className={`${styles.listItem} ${active?.id === r.id ? styles.listItemActive : ''}`}
                      onClick={() => setActive(r)}
                    >
                      <div className="fw-semibold">{r.title}</div>
                      <Badge bg="secondary" className="me-1">
                        {r.category}
                      </Badge>
                      {r.draft && <Badge bg="warning" text="dark">Draft</Badge>}
                      <div className="small text-muted">{new Date(r.lastUpdated).toLocaleDateString()} · 👍 {r.usefulCount}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </div>
        <div className={styles.viewCol}>
          {active ? (
            <Card>
              <Card.Body>
                <h2 className="h4">{active.title}</h2>
                <Badge className="me-2">{active.category}</Badge>
                <span className="text-muted small">Updated {new Date(active.lastUpdated).toLocaleString()}</span>
                <ol className="mt-3">
                  {(active.steps || []).map((s, i) => (
                    <li key={i} className="mb-2">
                      {s}
                    </li>
                  ))}
                </ol>
                <div className={styles.screenshot}>Screenshot placeholder</div>
                <h6>Related actions</h6>
                <ul>
                  {(active.relatedLinks || []).map((l) => (
                    <li key={l.path}>
                      <Link to={platformUrl(l.path)}>{l.label}</Link>
                    </li>
                  ))}
                </ul>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline-primary"
                    onClick={async () => {
                      try {
                        const res = await markRunbookUsefulAPI(active.id);
                        toast.success(`Thanks! (${res.data.usefulCount})`);
                        load();
                      } catch (e) {
                        toast.error(e.message);
                      }
                    }}
                  >
                    Mark as useful 👍
                  </Button>
                  {canEdit && (
                    <Button variant="link" className="p-0" onClick={() => toast.info('Edit runbook (simulated)')}>
                      Edit
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          ) : (
            <p className="text-muted">Select a runbook</p>
          )}
        </div>
      </div>
    </div>
  );
}
